import React from 'react'
import { useTranslation } from 'react-i18next'

interface PriceBreakdownCardProps {
  netPrice: number
  vatAmount: number
  vatRate: number
  grossPrice: number
  currencySymbol: string
}

const formatMoney = (value: number): string => value.toFixed(2)

const cardShadow =
  '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'

const mutedAmountClass = 'text-foregrounds-fg-muted text-sm font-medium font-Montserrat leading-5'
const grossAmountClass = 'text-foregrounds-fg-base text-xl font-bold font-Montserrat leading-5'

const MoneyAmount = ({
  amount,
  currencySymbol,
  className
}: {
  amount: string
  currencySymbol: string
  className: string
}): JSX.Element => (
  <div className="inline-flex justify-start items-center gap-2">
    <div className={className}>{amount}</div>
    <div className={className}>{currencySymbol}</div>
  </div>
)

const PriceBreakdownCard = ({
  netPrice,
  vatAmount,
  vatRate,
  grossPrice,
  currencySymbol
}: PriceBreakdownCardProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <div
      className="self-stretch shrink-0 bg-white rounded-lg flex flex-col justify-center items-start overflow-hidden"
      style={{ boxShadow: cardShadow }}>
      <div className="self-stretch px-5 py-4 flex flex-col justify-start items-start">
        <div className="self-stretch flex flex-col justify-start items-start gap-3">
          <div className="self-stretch flex flex-col justify-start items-start gap-2">
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="text-foregrounds-fg-base text-sm font-medium font-Montserrat leading-5">
                {t('sell_contract.net_price')}
              </div>
              <MoneyAmount
                amount={formatMoney(netPrice)}
                currencySymbol={currencySymbol}
                className={mutedAmountClass}
              />
            </div>
            <div className="self-stretch inline-flex justify-between items-center">
              <div className="text-foregrounds-fg-base text-sm font-medium font-Montserrat leading-5">
                {t('sell_contract.vat', { rate: vatRate })}
              </div>
              <MoneyAmount
                amount={formatMoney(vatAmount)}
                currencySymbol={currencySymbol}
                className={mutedAmountClass}
              />
            </div>
          </div>
          <div className="self-stretch h-px bg-borders-border-base" />
          <div className="self-stretch inline-flex justify-between items-center">
            <div className="text-foregrounds-fg-base text-xl font-bold font-Montserrat leading-5">
              {t('sell_contract.gross_price')}
            </div>
            <MoneyAmount
              amount={formatMoney(grossPrice)}
              currencySymbol={currencySymbol}
              className={grossAmountClass}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default PriceBreakdownCard
